import { useMemo, useState } from "react";
import { CHANNELS, NEWS, type NewsItem } from "../../../mocks/newsData";

const TABS = [
  { id: "all", label: "全部焦点" },
  { id: "society", label: "社会现场" },
  { id: "tech", label: "科技前哨" },
  { id: "market", label: "消费风向" },
  { id: "life", label: "生活" },
  { id: "culture", label: "文化娱乐" },
];

type Props = {
  onOpen: (id: string) => void;
};

export default function LeadPanel({ onOpen }: Props) {
  const [channel, setChannel] = useState("all");
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(6);

  const filtered = useMemo(() => {
    let list = channel === "all" ? NEWS : NEWS.filter((n) => n.channel === channel);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (n) =>
          n.title.toLowerCase().includes(q) ||
          n.excerpt.toLowerCase().includes(q) ||
          n.keywords.some((k) => k.includes(q)) ||
          n.channel.includes(q)
      );
    }
    return list;
  }, [channel, query]);

  const lead: NewsItem | null = filtered[0] || null;
  const rest = filtered.slice(1);
  const showList = (lead ? rest : filtered).slice(0, visible);
  const hasMore = (lead ? rest : filtered).length > visible;

  const switchChannel = (id: string) => {
    setChannel(id);
    setVisible(6);
  };

  return (
    <div>
      {/* 栏目头 */}
      <div className="flex flex-col gap-3 border-b-2 border-foreground-950 pb-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="h-5 w-1 rounded-full bg-primary-500" aria-hidden="true" />
            <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">焦点速递</h2>
            <span className="rounded-sm bg-primary-500 px-2 py-0.5 font-label text-[11px] font-bold uppercase tracking-wider text-background-50">
              头版
            </span>
          </div>
          <p className="mt-1 text-sm text-foreground-500">全网热点一屏聚合，最新动态持续刷新中</p>
        </div>

        <div className="relative w-full lg:w-64">
          <i
            className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"
            aria-hidden="true"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setVisible(6);
            }}
            placeholder="搜索热点关键词…"
            className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            aria-label="搜索热点"
          />
        </div>
      </div>

      {/* 栏目切换 */}
      <div className="mt-4 flex flex-wrap items-center gap-2" role="tablist" aria-label="栏目切换">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={channel === tab.id}
            onClick={() => switchChannel(tab.id)}
            className={
              channel === tab.id
                ? "cursor-pointer rounded-full bg-foreground-950 px-4 py-1.5 text-sm font-medium text-background-50"
                : "cursor-pointer rounded-full bg-background-100 px-4 py-1.5 text-sm font-medium text-foreground-600 transition-colors hover:bg-background-200"
            }
          >
            {tab.label}
          </button>
        ))}
        <span className="ml-auto hidden text-xs text-foreground-400 sm:block">
          共 <strong className="font-label text-foreground-700">{filtered.length}</strong> 条动态
        </span>
      </div>

      {/* 导读卡片 */}
      <div className="mt-4">
        {lead && !query && channel === "all" ? (
          <button
            type="button"
            onClick={() => onOpen(lead.id)}
            className="group relative block w-full cursor-pointer overflow-hidden rounded-lg text-left"
          >
            <div className="relative h-56 overflow-hidden rounded-lg md:h-[340px]">
              <img
                src={lead.cover}
                alt={lead.title}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" aria-hidden="true" />
              <div className="absolute left-4 top-4 flex items-center gap-2 md:left-6 md:top-5">
                {lead.urgent && (
                  <span className="flex items-center gap-1 rounded-sm bg-primary-500 px-2 py-0.5 text-[11px] font-bold text-background-50">
                    <i className="ri-alarm-fill" aria-hidden="true" /> 紧急
                  </span>
                )}
                <span className="rounded-sm bg-background-950/70 px-2 py-0.5 text-[11px] font-medium text-background-50">
                  {CHANNELS.find((c) => c.id === lead.channel)?.label}
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                <span className="mb-2 inline-block rounded-sm bg-primary-500 px-2 py-0.5 text-[11px] font-bold text-background-50">
                  {lead.badge}
                </span>
                <h3 className="max-w-3xl font-heading text-xl font-bold leading-snug text-background-50 md:text-3xl">
                  {lead.title}
                </h3>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-background-50/80">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-3-line" aria-hidden="true" /> {lead.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line" aria-hidden="true" /> {lead.timeText}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line" aria-hidden="true" /> {lead.views.toLocaleString("zh-CN")} 阅读
                  </span>
                </div>
              </div>
            </div>
          </button>
        ) : null}

        {/* 资讯列表 */}
        <div className="mt-4 space-y-3">
          {showList.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onOpen(item.id)}
              className="group flex w-full cursor-pointer items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-3 text-left transition-all hover:border-primary-300 hover:bg-primary-50/50"
            >
              <div className="relative hidden h-24 w-40 shrink-0 overflow-hidden rounded-md sm:block md:h-28 md:w-52">
                <img
                  src={item.cover}
                  alt={item.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {item.urgent && (
                  <span className="absolute left-1.5 top-1.5 flex items-center gap-1 rounded-sm bg-primary-500 px-1.5 py-0.5 text-[10px] font-bold text-background-50">
                    <i className="ri-alarm-fill" aria-hidden="true" /> 紧急
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-[11px]">
                  <span className="rounded-sm bg-secondary-100 px-1.5 py-0.5 font-medium text-secondary-900">
                    {CHANNELS.find((c) => c.id === item.channel)?.label}
                  </span>
                  {item.hot && (
                    <span className="flex items-center gap-1 text-primary-600">
                      <i className="ri-fire-fill" aria-hidden="true" /> 热
                    </span>
                  )}
                  <span className="text-foreground-400">{item.timeText}</span>
                </div>
                <h3 className="mt-1.5 line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
                  {item.title}
                </h3>
                <p className="mt-1 hidden line-clamp-2 text-sm leading-6 text-foreground-600 lg:block">
                  {item.excerpt}
                </p>
                <div className="mt-2 flex items-center gap-3 text-xs text-foreground-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line" aria-hidden="true" /> {item.views.toLocaleString("zh-CN")}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line" aria-hidden="true" /> {item.readMin} 分钟
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100/60 py-12 text-center">
            <i className="ri-inbox-2-line text-3xl text-background-400" aria-hidden="true" />
            <p className="mt-2 text-sm text-foreground-500">没有匹配的结果，换个关键词试试</p>
          </div>
        )}

        {hasMore && (
          <div className="mt-5 text-center">
            <button
              type="button"
              onClick={() => setVisible((v) => v + 6)}
              className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-6 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-foreground-950 hover:bg-foreground-950 hover:text-background-50"
            >
              加载更多动态
            </button>
          </div>
        )}
      </div>
    </div>
  );
}